import { cva, type VariantProps } from 'class-variance-authority';
import { focusRing, transitionBase, uiMarker, uiMarkerLayout } from './shared';

/**
 * TABS
 *
 * Two appearances, because the two uses are genuinely different:
 *   • `pill` — a segmented control on a muted track. For switching a view
 *     inside a card, where the tabs are a control among other controls.
 *   • `underline` — for top-level page sections, where the tabs are the
 *     navigation and shouldn't look like a button group.
 */
/**
 * The root. Unstyled unless it has to fill a height: `fill` makes it the
 * column that takes the leftover room in a `Page fill`, so the open panel
 * can scroll inside the tabs rather than push the page down. `lg` does the
 * same from `lg` up only, to match `Page fill="lg"`.
 */
export const tabsRecipe = cva([uiMarker, uiMarkerLayout], {
  variants: {
    fill: {
      true: 'flex min-h-0 flex-1 flex-col',
      lg: 'lg:flex lg:min-h-0 lg:flex-1 lg:flex-col',
      false: '',
    },
  },
  defaultVariants: { fill: false },
});
export type TabsRecipeProps = VariantProps<typeof tabsRecipe>;

export const tabsListRecipe = cva([uiMarker, 'inline-flex items-center'], {
  variants: {
    appearance: {
      pill: 'gap-1 rounded-lg bg-muted p-1',
      quiet: 'gap-2',
      underline: 'gap-4 border-b border-border w-full',
    },
    fullWidth: {
      true: 'w-full',
      false: '',
    },
  },
  defaultVariants: { appearance: 'pill', fullWidth: false },
});

export const tabsTriggerRecipe = cva(
  [
    uiMarker,
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-sans text-body font-medium disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:size-icon-sm [&_svg]:shrink-0',
    transitionBase,
    focusRing,
  ],
  {
    variants: {
      appearance: {
        pill: [
          'rounded-md px-3 py-1.5 text-muted-foreground',
          'hover:text-foreground',
          // Radix sets data-state=active on the selected trigger.
          'data-[state=active]:bg-card data-[state=active]:text-foreground',
          'data-[state=active]:shadow-e1',
        ].join(' '),
        quiet:
          'rounded-md px-2 py-1 text-body-sm text-muted-foreground hover:text-foreground data-[state=active]:bg-muted data-[state=active]:text-foreground',
        underline: [
          'border-b-2 border-transparent px-1 pb-2.5 pt-1 text-muted-foreground',
          'hover:text-foreground',
          'data-[state=active]:border-primary data-[state=active]:text-foreground',
        ].join(' '),
      },
      fullWidth: {
        true: 'flex-1',
        false: '',
      },
    },
    defaultVariants: { appearance: 'pill', fullWidth: false },
  }
);

export const tabsContentRecipe = cva([uiMarker, uiMarkerLayout, focusRing], {
  variants: {
    /**
     * The room between the list and the panel, on the layout gap scale.
     * `0` for a list that is a bar of its own — the impound detail pane
     * puts the panel in a padded body under it, and the default gap was
     * doubling that inset, which six panels there undid with `mt-0`.
     */
    gap: {
      0: 'mt-0',
      4: 'mt-4',
    },
    /**
     * The open panel takes the leftover height of a `Tabs fill`, as a column.
     * Scoped to the open panel: Radix keeps the others mounted under
     * `hidden`, and a plain `flex` would beat that and leave an empty panel
     * eating half the height. `lg` is the same from `lg` up only.
     */
    fill: {
      true: 'min-h-0 flex-1 flex-col data-[state=active]:flex',
      lg: 'lg:min-h-0 lg:flex-1 lg:flex-col lg:data-[state=active]:flex',
      false: '',
    },
  },
  defaultVariants: { gap: 4, fill: false },
});

export type TabsListRecipeProps = VariantProps<typeof tabsListRecipe>;
export type TabsContentRecipeProps = VariantProps<typeof tabsContentRecipe>;
